import I18n from 'i18n-js';
import _get from 'lodash/get';
import { humanizeTimestamp, processTimeSlot } from 'helper/anytime-booking';
import { SERVICE_TYPE } from 'constants/booking-constants';
import { GE_WEB_ORDER_STATE } from 'constants/order-constants';
import moment from 'moment';

const pickedUpBaseTime = 5000000000;
const notAvailableTime = 9000000000;

export const parseFirstTimeString = str => {
  if (!str) {
    return undefined;
  }
  const parts = str.split('-');
  if (!parts || parts.length < 1) {
    return undefined;
  }

  const firstTimeString = parts[0].trim().toLocaleUpperCase();
  if (firstTimeString) {
    if (firstTimeString.includes('AM') || firstTimeString.includes('PM')) {
      const time12 = moment(firstTimeString, 'h:mm A');
      if (time12.isValid()) {
        return time12;
      }
    }

    const time24 = moment(firstTimeString, 'H:mm');
    if (time24.isValid()) {
      return time24;
    }
  }

  return undefined;
};

// pick up ETA > schedule slot > ASAP > picked up time
export const getPickUpTimeForSorting = order => {
  if (!order) {
    return notAvailableTime;
  }

  const pickedUpTimestamp = _get(
    _get(order, 'orderEvents', []).find(oe => oe.State === 'DROPPING_OFF'),
    'Timestamp',
  );
  if (pickedUpTimestamp) {
    return pickedUpBaseTime + pickedUpTimestamp;
  }

  const orderPickUpETA = _get(order, 'pickUpTime', '');
  const parsedTime = parseFirstTimeString(orderPickUpETA);
  if (parsedTime) {
    return parsedTime.unix();
  }

  if (
    !orderPickUpETA &&
    order.scheduledPickupSlot &&
    order.scheduledPickupSlot.from
  ) {
    const pickUpFrom = moment(order.scheduledPickupSlot.from);
    return pickUpFrom.unix();
  }

  return moment()
    .endOf('day')
    .unix();
};

export const pickUpTimeSorter = (orderA, orderB) =>
  getPickUpTimeForSorting(orderA) - getPickUpTimeForSorting(orderB);

const PickupTime = ({ scheduledPickupSlot, order, countryCode }) => {
  if (!order) {
    return null;
  }

  // 1. show picked up time if it's already picked up
  const pickedUpTimestamp = _get(
    _get(order, 'orderEvents', []).find(
      oe => oe.State === 'DROPPING_OFF' || oe.State === 'PENDING_DROP_OFF',
    ),
    'Timestamp',
  );
  if (pickedUpTimestamp) {
    const { dateLabel, timeLabel } = humanizeTimestamp({
      timestamp: pickedUpTimestamp,
      countryCode,
    });
    return `${I18n.t(
      'v2.anytimeBooking.pickedUpAt',
    )} ${dateLabel}, ${timeLabel}`;
  }

  const orderPickUpETA = _get(order, 'pickUpTime', '').toLocaleLowerCase();
  if (orderPickUpETA) {
    // 3. SameDay ASAP Pickup
    const isSameDay =
      _get(order, 'service.expressServiceID') === SERVICE_TYPE.SAMEDAY;
    const isScheduled = _get(order, 'status') === GE_WEB_ORDER_STATE.Scheduled;
    if (isSameDay && isScheduled) {
      return `${I18n.t('v2.anytimeBooking.today')}, ${I18n.t(
        'v2.anytimeBooking.pickUpBy',
        { eta: orderPickUpETA },
      )}`;
    }

    // 4. picking up eta
    return `${I18n.t('v2.anytimeBooking.today')}, ${orderPickUpETA}`;
  }

  // 2. for scheduled booking
  if (scheduledPickupSlot && !orderPickUpETA) {
    const { pickUpRange, dateLabels } = processTimeSlot({
      timeSlot: scheduledPickupSlot,
      countryCode,
    });
    return `${dateLabels.weekday}, ${pickUpRange}`;
  }

  // 5. Instant ASAP Pickup
  return I18n.t('v2.anytimeBooking.ASAP');
};

export default PickupTime;
